/**
 * Shared `ShortcutsSlot` implementation (NAVX-5236) for the Jira GSN
 * invited-app-shortcuts integration.
 *
 * Instead of wrapping the whole `GlobalSideNavigation` in
 * `InvitedAppShortcutsContainer` and swapping its `shortcuts` config, GSN keeps
 * the base `shortcuts` config and renders this component in its `ShortcutsSlot`.
 * GSN hands us the already-resolved `shortcutItems` (visibility-filtered,
 * ordered, analytics-wrapped) plus a pre-bound `Component`. We feed those items
 * to `InvitedAppShortcutsContainer` as its `shortcutsConfig` so it can decorate
 * the eligible invited-app entries in place (hover popup ref, activation
 * onClick, invited label), then render the decorated result back through
 * `Component`.
 *
 * The container config (resolver result + mode-specific props) reaches this
 * module-level component via React context, so the slot keeps a stable identity
 * and GSN never remounts the shortcuts subtree when the config reference
 * changes. Both call sites (the experiment/legacy branch in
 * `GSNForContentSidebarEntryPoint.tsx` and the productionisation path in
 * `GSNForContentSidebarWithInvitedAppRecommendations.tsx`) share this slot.
 *
 * Config keys are opaque here — we read the values straight back via
 * `Object.values`, so we don't depend on `menuItemId` being present on every
 * item variant, and insertion order (hence GSN's visible order) is preserved.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutAllowedTargetAppKey,
} from '@atlassian/cross-join-nudges/types';
import type { UseInvitedAppShortcutsResult } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';

/**
 * Container config carried to the slot via context. Discriminated on `mode` so
 * the slot can render the matching `InvitedAppShortcutsContainer` variant:
 *   - `experiment`: legacy cookie + traits BFF path (default container mode).
 *   - `productionisation`: AGG-recommendations path, which additionally needs
 *     `cloudId`, `userId`, `recommendedApps`, and `sourceApp`.
 */
export type InvitedAppShortcutsSlotConfig =
	| {
			mode: 'experiment';
			resolverResult: UseInvitedAppShortcutsResult;
	  }
	| {
			mode: 'productionisation';
			resolverResult: UseInvitedAppShortcutsResult;
			cloudId: string;
			userId: string;
			recommendedApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
	  };

export const InvitedAppShortcutsSlotContext: React.Context<InvitedAppShortcutsSlotConfig | null> =
	createContext<InvitedAppShortcutsSlotConfig | null>(null);

export function InvitedAppShortcutsSlot({
	Component,
	shortcutItems,
}: ShortcutsSlotProps): React.JSX.Element {
	const config = useContext(InvitedAppShortcutsSlotContext);

	const shortcutsConfig = useMemo(
		() =>
			shortcutItems.reduce<ShortcutsConfig>((acc, item, index) => {
				acc[String(index)] = item;
				return acc;
			}, {}),
		[shortcutItems],
	);

	// Defensive: both call sites always supply a config on this path, but if it
	// is ever missing, fail open to the default (undecorated) shortcuts menu
	// rather than crashing the nav.
	if (!config) {
		return <Component />;
	}

	if (config.mode === 'productionisation') {
		return (
			<InvitedAppShortcutsContainer
				mode="productionisation"
				cloudId={config.cloudId}
				userId={config.userId}
				recommendedApps={config.recommendedApps}
				sourceApp={config.sourceApp}
				shortcutsConfig={shortcutsConfig}
				resolverResult={config.resolverResult}
			>
				{({ referrableShortcuts }) => (
					<Component shortcutItems={Object.values(referrableShortcuts)} />
				)}
			</InvitedAppShortcutsContainer>
		);
	}

	return (
		<InvitedAppShortcutsContainer
			shortcutsConfig={shortcutsConfig}
			resolverResult={config.resolverResult}
		>
			{({ referrableShortcuts }) => (
				<Component shortcutItems={Object.values(referrableShortcuts)} />
			)}
		</InvitedAppShortcutsContainer>
	);
}
